import { flexRender, type Column } from '@tanstack/react-table';
import {
	Table,
	IconButton,
	Input,
	Text,
	Flex,
	InputGroup,
	ButtonGroup,
	Pagination,
	Select,
	createListCollection,
	Portal,
	Menu,
	Button,
	EmptyState,
	Box,
	Badge,
	Dialog,
	Float,
	CloseButton,
	Span,
} from '@chakra-ui/react';
import { IoOptionsOutline, IoSearch, IoArrowDown, IoArrowUp, IoArrowBack, IoArrowForward, IoFilterOutline, IoPlayBack, IoPlayForward } from 'react-icons/io5';
import { type DataCollectionContextProps } from './DataCollection.types';
import { useDebounceCallback } from 'usehooks-ts';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@chakra-ui/react';
import { parseErrorToString } from '../utils/parseErrorToString';
import { IoFileTrayOutline, IoSearchOutline, IoCloseCircle } from 'react-icons/io5';
import { useMobileContext } from '../context/MobileContext';
import { MemoizedFilter } from './Filter';
import { RefreshButton } from './RefreshButton';

const CLIENT_SIDE_SEARCH_DEBOUNCE_MS = 100;
const SERVER_SIDE_SEARCH_DEBOUNCE_MS = 250;

export const createDataCollectionTable = <T extends Record<string, unknown>>(useDataCollection: () => DataCollectionContextProps<T>) => {
	const DataCollectionTableHeader = ({ isSearchShown = true }: { isSearchShown?: boolean }) => {
		const { t } = useTranslation();
		const { isMobile } = useMobileContext();
		const { isServerSide, table, search, isFetching, isSelectable, selectedRows, selectedItemsActions, pagination, setPagination } = useDataCollection();
		const debounced = useDebounceCallback(value => table.setGlobalFilter(String(value)), isServerSide ? SERVER_SIDE_SEARCH_DEBOUNCE_MS : CLIENT_SIDE_SEARCH_DEBOUNCE_MS);
		const columns = table.getAllFlatColumns();
		const activeFiltersCount = table.getState().columnFilters.flatMap(f => f.value).length;
		const selectedRowsArray = Object.keys(selectedRows);

		return (
			<Flex gap="2" flex={1} justifyContent={isMobile ? 'normal' : 'space-between'} flexWrap={'wrap'}>
				{isSelectable && (
					<Box flex={2}>
						<Menu.Root closeOnSelect={true}>
							<Menu.Trigger asChild>
								<Button paddingX="3" fontWeight={400} color={'black'} disabled={selectedRowsArray.length === 0} minW={'150px'} variant="outlineSecondary">
									{selectedRowsArray.length > 0 ? `${t('general.selected_rows')} (${selectedRowsArray.length})` : t('general.none_selected')}
								</Button>
							</Menu.Trigger>
							<Portal>
								<Menu.Positioner>
									<Menu.Content>
										{selectedItemsActions?.map(item => (
											<Menu.Item key={item.id} value={item.id} onClick={() => item.callbackFn(selectedRowsArray, table)}>
												{item.label}
											</Menu.Item>
										))}
										<Menu.Item value="clear" onClick={() => table.resetRowSelection()}>
											{t('general.clear')}
										</Menu.Item>
									</Menu.Content>
								</Menu.Positioner>
							</Portal>
						</Menu.Root>
					</Box>
				)}
				{isMobile && columns.filter(col => col.columnDef.meta?.filterVariant).length > 0 ? (
					<Dialog.Root size="cover">
						<Dialog.Trigger asChild>
							<IconButton position="relative" variant={'outlineSecondary'}>
								<IoFilterOutline />
								{activeFiltersCount > 0 && (
									<Float offsetX="2px" offsetY="2px">
										<Badge variant="solid">{activeFiltersCount}</Badge>
									</Float>
								)}
							</IconButton>
						</Dialog.Trigger>
						<Dialog.Backdrop />
						<Dialog.Positioner>
							<Dialog.Content>
								<Dialog.Header>
									<Dialog.Title>{t('general.filters')}</Dialog.Title>
								</Dialog.Header>
								<Dialog.Body>
									<Flex flexDir="column" gap="2">
										{columns.map(col => {
											if (col.columnDef.meta?.filterVariant) return <MemoizedFilter<T> key={col.id} column={col} isFetching={isFetching} />;
										})}
									</Flex>
								</Dialog.Body>
								<Dialog.Footer flexWrap="wrap">
									<Dialog.ActionTrigger asChild flexGrow={1}>
										<Button onClick={() => table.resetColumnFilters()} variant="outlineSecondary">
											{t('general.clear_all_filters')}
										</Button>
									</Dialog.ActionTrigger>
									<Dialog.ActionTrigger asChild flexGrow={1}>
										<Button variant="solid">{t('general.close')}</Button>
									</Dialog.ActionTrigger>
								</Dialog.Footer>
								<Dialog.CloseTrigger asChild>
									<CloseButton size="sm" />
								</Dialog.CloseTrigger>
							</Dialog.Content>
						</Dialog.Positioner>
					</Dialog.Root>
				) : (
					columns.filter(col => col.columnDef.meta?.filterVariant).length > 0 && (
						<Flex gap="2">
							{columns.map(col => {
								if (col.columnDef.meta?.filterVariant) return <MemoizedFilter<T> isFetching={isFetching} key={col.id} column={col} />;
							})}
						</Flex>
					)
				)}

				<Flex gap="2">
					<Menu.Root closeOnSelect={false}>
						<Menu.Trigger data-cy="dataCollectionTableOptions" asChild>
							<IconButton variant="outlineSecondary">
								<IoOptionsOutline />
							</IconButton>
						</Menu.Trigger>
						<Portal>
							<Menu.Positioner>
								<Menu.Content>
									<Menu.ItemGroup>
										<Menu.ItemGroupLabel>{t('general.choose_visible_columns')}</Menu.ItemGroupLabel>
										{table
											.getAllLeafColumns()
											.filter(col => col.accessorFn)
											.map(col => (
												<Menu.CheckboxItem value={col.id} key={col.id} checked={col.getIsVisible()} onCheckedChange={() => col.toggleVisibility()}>
													{col?.columnDef?.header?.toString()}
													<Menu.ItemIndicator />
												</Menu.CheckboxItem>
											))}
									</Menu.ItemGroup>
								</Menu.Content>
							</Menu.Positioner>
						</Portal>
					</Menu.Root>

					{isSearchShown ? (
						<InputGroup flex="1" minW="120px" startElement={<IoSearch />}>
							<Input
								defaultValue={search}
								onChange={event => {
									setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
									debounced(event.target.value);
								}}
								placeholder={t('general.search')}
							/>
						</InputGroup>
					) : null}
					<RefreshButton useDataCollection={useDataCollection} />
				</Flex>
			</Flex>
		);
	};

	const DataCollectionTable = () => {
		const { t } = useTranslation();
		const {
			paginationServerState,
			selectedRows,
			table,
			isFetching,
			error,
			onClickRow,
			emptyDataText,
			emptyDataIcon: EmptyDataIcon,
			search,
			isPending,
			selectAllFn,
			parsedApiFilters,
		} = useDataCollection();
		const headerGroups = table.getHeaderGroups();
		const rows = table.getRowModel().rows;

		const isAllPageRowsSelected = table.getIsAllPageRowsSelected?.() ?? table.getPaginationRowModel().rows.every(row => row.getIsSelected());
		const getSortingText = (column: Column<T, unknown>) => {
			if (!column.getCanSort()) return undefined;
			if (column.getNextSortingOrder() === 'asc') return t('general.sort_asc');
			return column.getNextSortingOrder() === 'desc' ? t('general.sort_desc') : t('general.clear');
		};

		function renderView() {
			if (isPending) {
				return Array.from({ length: 4 }).map((_, rowIndex) => (
					<Table.Row key={`skeleton-row-${rowIndex}`}>
						{/* eslint-disable-next-line no-shadow*/}
						{headerGroups[0]?.headers.map((_, colIndex) => (
							<Table.Cell key={`skeleton-cell-${rowIndex}-${colIndex}`}>
								<Skeleton height="16px" width="80%" borderRadius="md" />
							</Table.Cell>
						))}
					</Table.Row>
				));
			}

			return rows.map(row => (
				<Table.Row onClick={onClickRow ? () => onClickRow(row) : undefined} key={row.id}>
					{row.getVisibleCells().map(cell => {
						const shouldStopClick = cell.column.columnDef.id === 'checkbox-header';
						return (
							<Table.Cell
								data-cy={cell.id}
								overflow={'hidden'}
								textOverflow={'ellipsis'}
								cursor={onClickRow && !shouldStopClick ? 'pointer' : 'auto'}
								onClick={
									shouldStopClick
										? e => {
												e.stopPropagation();
												row.toggleSelected();
											}
										: undefined
								}
								key={cell.id}
								title={cell.column.id === 'declineReason' ? (cell.getValue() as string) : undefined}
							>
								<Skeleton loading={isFetching}>
									<div
										style={{
											maxWidth: cell.column.columnDef.maxSize,
											overflow: 'hidden',
											whiteSpace: 'nowrap',
											textOverflow: 'ellipsis',
											display: 'block',
										}}
									>
										{flexRender(cell.column.columnDef.cell, cell.getContext())}
									</div>
								</Skeleton>
							</Table.Cell>
						);
					})}
				</Table.Row>
			));
		}

		if (error) {
			return (
				<EmptyState.Root flex={1}>
					<EmptyState.Content gap={{ mobileDown: 3, base: 4 }}>
						<EmptyState.Indicator>{<IoCloseCircle color="red" />}</EmptyState.Indicator>
						<EmptyState.Title>{t('general.error_occurred')}</EmptyState.Title>
						<EmptyState.Description wordBreak="break-word" textAlign="center">
							{parseErrorToString(error)}
						</EmptyState.Description>
					</EmptyState.Content>
				</EmptyState.Root>
			);
		}

		if (!isFetching && rows.length === 0) {
			return (
				<EmptyState.Root flex={1}>
					<EmptyState.Content gap={{ mobileDown: 3, base: 4 }}>
						{search ? (
							<>
								<EmptyState.Indicator>{<IoSearchOutline />}</EmptyState.Indicator>
								<EmptyState.Description wordBreak="break-word" textAlign="center">
									{t('general.no_search_results_with')} "{search}"
								</EmptyState.Description>
							</>
						) : (
							<>
								<EmptyState.Indicator>{EmptyDataIcon ? <EmptyDataIcon /> : <IoFileTrayOutline />}</EmptyState.Indicator>
								<EmptyState.Description textAlign="center">{emptyDataText ?? t('general.no_data')}</EmptyState.Description>
							</>
						)}
					</EmptyState.Content>
				</EmptyState.Root>
			);
		}

		return (
			<Table.ScrollArea maxW="full" height="100%">
				<Flex
					borderRadius="8px"
					height={isAllPageRowsSelected && selectAllFn ? '25px' : '0px'}
					gap={1}
					marginBottom={1}
					backgroundColor="yellow.300"
					color="yellow.700"
					width="100%"
					justifyContent={'center'}
					transition={'height 0.5s'}
					overflow={'hidden'}
				>
					<Text>{paginationServerState?.total === Object.keys(selectedRows).length ? t('general.all_selected') : t('general.all_on_page_selected')}</Text>
					<Text
						onClick={async () => {
							if (paginationServerState?.total === Object.keys(selectedRows).length) {
								table.setRowSelection({});
							} else {
								if (selectAllFn) {
									const response = await selectAllFn(parsedApiFilters);
									const selection = Object.fromEntries(response.map(id => [id, true]));
									table.setRowSelection(selection);
								}
							}
						}}
						cursor="pointer"
						textDecor={'underline'}
					>
						{paginationServerState?.total === Object.keys(selectedRows).length ? t('general.clear_selection') : t('general.select_all')}{' '}
					</Text>
				</Flex>

				<Table.Root striped interactive={!!onClickRow}>
					<Table.Header>
						{headerGroups.map(headerGroup => (
							<Table.Row key={headerGroup.id}>
								{headerGroup.headers.map(header => {
									return (
										<Table.ColumnHeader key={header.id} colSpan={header.colSpan}>
											{header.isPlaceholder ? null : (
												<Flex
													align="center"
													gap={1}
													cursor={header.column.getCanSort() ? 'pointer' : 'normal'}
													onClick={() => {
														if (header.column.getCanSort() === false) {
															return;
														}
														const isSorted = header.column.getIsSorted();

														if (isSorted === 'asc') {
															header.column.toggleSorting(true); // → desc
														} else {
															header.column.toggleSorting(false); // → asc
														}
													}}
													title={getSortingText(header.column)}
												>
													{flexRender(header.column.columnDef.header, header.getContext())}
													{{
														asc: <IoArrowUp />,
														desc: <IoArrowDown />,
													}[header.column.getIsSorted() as string] ?? null}
												</Flex>
											)}
										</Table.ColumnHeader>
									);
								})}
							</Table.Row>
						))}
					</Table.Header>
					<Table.Body maxH="full" overflowY="auto">
						{renderView()}
					</Table.Body>
				</Table.Root>
			</Table.ScrollArea>
		);
	};

	const DataCollectionTableFooter = () => {
		const { t } = useTranslation();
		const { pagination, setPagination, paginationServerState, isServerSide, table } = useDataCollection();
		const rowCount = isServerSide ? paginationServerState?.total : table.getRowCount();
		const { isMobile } = useMobileContext();

		return (
			<Flex gap="1" flexWrap="wrap-reverse">
				<Flex flex={1} gap="2" alignItems={'center'} flexWrap={isMobile ? 'wrap' : 'nowrap'}>
					<Select.Root
						multiple={false}
						collection={itemsPerPageCollection}
						value={[pagination.pageSize.toString()]}
						onValueChange={e => setPagination(prev => ({ ...prev, pageSize: Number(e.value) }))}
						minW={'75px'}
						variant="outline"
					>
						<Select.HiddenSelect />
						<Select.Control>
							<Select.Trigger>
								<Select.ValueText />
							</Select.Trigger>
							<Select.IndicatorGroup>
								<Select.Indicator />
							</Select.IndicatorGroup>
						</Select.Control>
						<Portal>
							<Select.Positioner className="footerItemsSelect">
								<Select.Content>
									{itemsPerPageCollection.items.map(item => (
										<Select.Item item={item} key={item.value}>
											{item.label}
											<Select.ItemIndicator />
										</Select.Item>
									))}
								</Select.Content>
							</Select.Positioner>
						</Portal>
					</Select.Root>
					<Pagination.Root
						count={rowCount}
						pageSize={pagination.pageSize}
						defaultPage={rowCount === 0 ? 0 : 1}
						page={rowCount === 0 ? 0 : pagination.pageIndex + 1}
						onPageChange={e => (isServerSide ? setPagination(prev => ({ ...prev, pageIndex: e.page - 1 })) : table.setPageIndex(e.page - 1))}
					>
						<ButtonGroup gap="" size="sm" width="fit-content" whiteSpace="nowrap">
							<IconButton
								variant="outlineSecondary"
								disabled={rowCount === 1 || pagination.pageIndex === 0}
								onClick={() => (isServerSide ? setPagination(prev => ({ ...prev, pageIndex: 0 })) : table.setPageIndex(0))}
							>
								<IoPlayBack />
							</IconButton>
							<Pagination.PrevTrigger disabled={rowCount === 0} asChild>
								<IconButton variant="outlineSecondary">
									<IoArrowBack />
								</IconButton>
							</Pagination.PrevTrigger>
							<Pagination.PageText fontWeight={300} format="short" />
							<Pagination.NextTrigger disabled={rowCount === 0} asChild>
								<IconButton variant="outlineSecondary">
									<IoArrowForward />
								</IconButton>
							</Pagination.NextTrigger>
							<IconButton
								variant="outlineSecondary"
								disabled={!rowCount || rowCount === 0 || pagination.pageIndex >= Math.ceil(rowCount / pagination.pageSize) - 1}
								onClick={() => {
									if (!rowCount) return;
									const lastPageIndex = Math.ceil(rowCount / pagination.pageSize) - 1;
									if (isServerSide) {
										setPagination(prev => ({ ...prev, pageIndex: lastPageIndex }));
									} else {
										table.setPageIndex(lastPageIndex);
									}
								}}
							>
								<IoPlayForward />
							</IconButton>
						</ButtonGroup>
					</Pagination.Root>
					{rowCount ? <Span whiteSpace={'nowrap'}>{`${rowCount} ${t('general.items')}`}</Span> : null}
				</Flex>
			</Flex>
		);
	};

	return { DataCollectionTable, DataCollectionTableHeader, DataCollectionTableFooter };
};

const itemsPerPageCollection = createListCollection({
	items: [
		{ label: '10', value: '10' },
		{ label: '25', value: '25' },
		{ label: '50', value: '50' },
		{ label: '100', value: '100' },
	],
});
